import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import { LogOut, User as UserIcon, ChevronDown, Settings, LayoutDashboard } from 'lucide-react';
import { userApi } from '../api';

const Navbar = ({ user, onLogout, onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking', 'online', 'offline'

  useEffect(() => {
    const checkServer = async () => {
      try {
        const res = await userApi.checkHealth();
        if (res.status === 200) {
          setServerStatus('online');
        } else {
          setServerStatus('offline');
        }
      } catch (err) {
        setServerStatus('offline');
      }
    };
    checkServer();
    const interval = setInterval(checkServer, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fonction utilitaire pour afficher l'avatar ou une icône par défaut
  const renderAvatar = () => {
    if (user?.pp_url) {
      return (
        <img
          src={user.pp_url}
          alt={user.display_name}
          className="w-9 h-9 rounded-full object-cover border border-gray-700 shadow-sm"
        />
      );
    }
    return (
      <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center border border-gray-700 text-gray-400">
        <UserIcon size={18} />
      </div>
    );
  };

  return (
    <nav className="bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-6 py-3 flex justify-between items-center sticky top-0 z-50">

      {/* 1. LOGO ET STATUT */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => onNavigate('dashboard')}
          className="group decoration-transparent hover:opacity-90 transition-opacity bg-transparent border-none text-left focus:outline-none"
        >
          <Logo size="text-xl" />
        </button>

        <div
          className="w-2.5 h-2.5 rounded-full transition-colors duration-300"
          style={{
            backgroundColor: serverStatus === 'online' ? '#34C759' : serverStatus === 'offline' ? '#FF3B30' : 'gray',
            boxShadow: `0 0 5px ${serverStatus === 'online' ? '#34C759' : serverStatus === 'offline' ? '#FF3B30' : 'gray'}`
          }}
          title={serverStatus === 'online' ? 'Connecté au serveur API' : serverStatus === 'offline' ? 'Serveur API injoignable' : 'Vérification...'}
        />
      </div>

      {/* 2. USER SECTION (DROITE) */}
      {user && (
        <div className="relative">

          {/* Bouton déclencheur du menu */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-3 p-1 rounded-full hover:bg-gray-800/50 transition-colors group focus:outline-none"
          >
            <div className="flex flex-col items-end hidden sm:flex">
              <span className="text-sm font-semibold text-white leading-none group-hover:text-blue-400 transition-colors">
                {user.display_name}
              </span>
            </div>
            {renderAvatar()}
            <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* MENU DÉROULANT (DROPDOWN) */}
          {isMenuOpen && (
            <>
              {/* Overlay invisible pour fermer le menu si on clique ailleurs */}
              <div
                className="fixed inset-0 z-10 cursor-default"
                onClick={() => setIsMenuOpen(false)}
              ></div>

              {/* Le Menu */}
              <div className="absolute right-0 top-full mt-2 w-56 bg-gray-800 rounded-xl shadow-xl border border-gray-700 z-20 overflow-hidden py-1">

                {/* En-tête du menu (info user) */}
                <div className="px-4 py-3 border-b border-gray-700/50">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Connecté en tant que</p>
                  <p className="text-sm font-bold text-white truncate">{user.username}</p>
                </div>

                {/* Liens du menu */}
                <div className="py-1">
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2 transition-colors"
                    onClick={() => { setIsMenuOpen(false); onNavigate('dashboard'); }}
                  >
                    <LayoutDashboard size={16} />
                    Tableau de Bord
                  </button>

                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2 transition-colors"
                    onClick={() => { setIsMenuOpen(false); onNavigate('settings'); }}
                  >
                    <Settings size={16} />
                    Paramètres
                  </button>

                  <button
                    className="w-full px-4 py-2 text-left text-sm text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 flex items-center gap-2 transition-colors"
                    onClick={() => { setIsMenuOpen(false); onNavigate('addon'); }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" x2="12" y1="15" y2="3" />
                    </svg>
                    Extension Web
                  </button>
                </div>

                <div className="border-t border-gray-700/50 py-1">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onLogout();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2 transition-colors"
                  >
                    <LogOut size={16} />
                    Déconnexion
                  </button>
                </div>

              </div>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;